import { useEffect, useRef, useState } from 'react';
import {
  createDictation, findRecognition, micOptions, silence, speak, spokenText, whenVoicesReady,
  type DictationController, type DictationState, type MicOption, type SynthesisLike, type UtteranceConstructor,
} from '../lib/speech';

const MIC_STORAGE_KEY = 'brunel.micDeviceId';

function readMicDeviceId(): string {
  try { return localStorage.getItem(MIC_STORAGE_KEY) ?? ''; } catch { return ''; }
}

function writeMicDeviceId(id: string): void {
  try { localStorage.setItem(MIC_STORAGE_KEY, id); } catch { /* storage unavailable or blocked */ }
}


function browserWindow(): Record<string, unknown> | undefined {
  return typeof window === 'undefined' ? undefined : (window as unknown as Record<string, unknown>);
}

/**
 * Microphone button that dictates into a text field. Final phrases are
 * appended through `onText` into the caller's draft. Interim recognition is
 * not echoed beside the microphone; dictated words stay in the textarea.
 * Dictated text is only a draft: nothing is sent until the owner presses Send.
 */
/**
 * `stopRef` lets the composer's one Send button finish a dictation it is sending:
 * the parent calls it to stop listening at the moment the dictated draft goes out.
 */
export function DictateButton({ onText, onInterim, lang, stopRef }: {
  onText: (text: string) => void; onInterim?: (text: string) => void; lang?: string;
  stopRef?: { current: (() => void) | null };
}) {
  const [state, setState] = useState<DictationState>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);
  const [micDeviceId, setMicDeviceId] = useState(readMicDeviceId);
  const controller = useRef<DictationController | null>(null);
  const onTextRef = useRef(onText);
  const onInterimRef = useRef(onInterim);
  const micDeviceIdRef = useRef(micDeviceId);
  onTextRef.current = onText;
  onInterimRef.current = onInterim;
  micDeviceIdRef.current = micDeviceId;

  async function getMicTrack(): Promise<{ stop(): void } | null> {
    const id = micDeviceIdRef.current;
    if (!id) return null;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: id } } });
    return stream.getAudioTracks()[0] ?? null;
  }

  useEffect(() => {
    const created = createDictation(findRecognition(browserWindow()), {
      onFinal: (text) => onTextRef.current(text),
      onInterim: (text) => onInterimRef.current?.(text),
      onState: (next, why) => { setState(next); setMessage(why ?? null); },
    }, { lang, getTrack: getMicTrack });
    controller.current = created;
    if (stopRef) stopRef.current = () => created.stop();
    setSupported(created.supported);
    return () => {
      if (stopRef && controller.current === created) stopRef.current = null;
      created.dispose();
    };
  }, [lang]);

  if (!supported) {
    return <small className="muted">Dictation is not available in this browser.</small>;
  }
  const listening = state === 'listening';
  return (
    <span className="dictate">
      <button
        type="button"
        className={listening ? 'mic live' : 'mic'}
        aria-pressed={listening}
        aria-label={listening ? 'Stop dictation' : 'Dictate'}
        title={listening ? 'Stop dictation' : 'Dictate'}
        onClick={() => (listening ? controller.current?.stop() : controller.current?.start())}
      >
        {listening ? <StopIcon /> : <MicIcon />}
      </button>
      <MicMenu
        selected={micDeviceId}
        onSelect={(id) => { writeMicDeviceId(id); micDeviceIdRef.current = id; setMicDeviceId(id); }}
      />
      {listening && <span className="muted" aria-live="polite">Listening…</span>}
      {message && <span role="alert">{message}</span>}
    </span>
  );
}

/**
 * Chevron next to the mic button that lists the machine's microphones and
 * remembers the chosen one. Browsers hide device labels until the user has
 * granted a one-time getUserMedia permission, so opening the menu requests
 * audio and immediately releases it.
 */
function MicMenu({ selected, onSelect }: { selected: string; onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<MicOption[]>([]);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const available = typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.enumerateDevices === 'function';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) setOptions(micOptions(devices));
      } catch { /* enumeration failed; keep the current list */ }
    };
    const unlockLabels = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((mediaTrack) => mediaTrack.stop());
      } catch { /* without permission labels stay blank; the list still shows */ }
      await refresh();
    };
    void unlockLabels();
    const onDeviceChange = () => void refresh();
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && event.target instanceof Node && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  if (!available) return null;
  return (
    <span ref={rootRef}>
      <button
        type="button"
        className="mic-menu-toggle"
        aria-label="Choose microphone"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <ChevronIcon />
      </button>
      {open && (
        <div className="mic-menu" role="menu">
          {options.map((option) => (
            <button
              key={option.deviceId}
              type="button"
              role="menuitemradio"
              aria-checked={option.deviceId === selected}
              onClick={() => { onSelect(option.deviceId); setOpen(false); }}
            >
              <span className="mic-menu-check" aria-hidden="true">{option.deviceId === selected ? '✓' : ''}</span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

function synthesisOf(): SynthesisLike | null {
  return (browserWindow()?.speechSynthesis ?? null) as SynthesisLike | null;
}

/**
 * Speak text aloud with the browser's built-in voice.
 *
 * Nothing is queued until the machine has handed over its voices: an utterance
 * given to an empty voice list is dropped without a sound, which is what the
 * first read of a freshly loaded page used to run into.
 */
export function speakText(text: string): boolean {
  const synthesis = synthesisOf();
  const Utterance = (browserWindow()?.SpeechSynthesisUtterance ?? null) as UtteranceConstructor | null;
  if (!synthesis || !Utterance) return false;
  if (spokenText(text) === '') return false;
  const lang = typeof navigator === 'undefined' ? undefined : navigator.language;
  whenVoicesReady(synthesis, () => { speak(synthesis, Utterance, text, lang); });
  return true;
}


/** Stop reading at once - used when the owner switches reading aloud back off. */
export function stopSpeaking(): void {
  silence(synthesisOf());
}

export function ReadAloudButton({ text }: { text: string }) {
  return (
    <button type="button" className="link" aria-label="Read aloud" onClick={() => speakText(text)}>
      🔊
    </button>
  );
}
